import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { 
  MapPin, Calendar, Clock, Users, Activity, AlertTriangle, 
  FileText, Brain, TrendingUp, Network, Target, Lightbulb,
  ChevronRight, Image, Siren, Lock, ArrowRight, CheckCircle2,
  Zap, Shield, User, RefreshCw, Loader2, Pencil, Save, X
} from "lucide-react";
import { 
  WorkplaceIncident, 
  INJURY_SEVERITY_LABELS, 
  AREA_SAFETY_LABELS,
  InjurySeverity,
  AreaSafetyStatus,
  AIExtractedContext,
  AIExtractedSignal
} from "@/types/incident";
import { useSubscriptionFeatures, useUserCompanyId } from "@/hooks/useSubscriptionFeatures";
import { TIER_NAMES } from "@/lib/subscriptionFeatures";

interface LinkedCluster {
  id: string;
  name: string;
  risk_score: number;
  worker_count: number;
}

interface PriorIntervention {
  id: string;
  title: string;
  type: string;
  target: string;
  status: string;
  delivered_at: string;
}

interface IncidentOverviewProps {
  incident: WorkplaceIncident;
  onRefresh: () => void;
}

// Hazard category labels for display
const HAZARD_CATEGORY_LABELS: Record<string, string> = {
  no_ppe: "Tidak Memakai APD",
  improper_lifting: "Teknik Angkat Tidak Benar",
  unsafe_procedure: "Prosedur Tidak Aman",
  slippery_floor: "Lantai Licin",
  spill: "Tumpahan",
  falling_object: "Benda Jatuh",
  electrical_hazard: "Bahaya Listrik",
  fire_hazard: "Bahaya Kebakaran",
  vehicle_hazard: "Bahaya Kendaraan",
  housekeeping: "Kebersihan Buruk",
  other: "Lainnya"
};

const MECHANISM_LABELS: Record<string, string> = {
  lifting: "Mengangkat Beban",
  struck_by: "Tertimpa/Terbentur",
  caught_in: "Terjepit",
  fall_same_level: "Terjatuh (Sama Level)",
  fall_from_height: "Terjatuh dari Ketinggian",
  chemical_exposure: "Paparan Kimia",
  electrical: "Tersengat Listrik",
  heat_burn: "Terbakar",
  cut_laceration: "Luka Potong",
  repetitive_motion: "Gerakan Berulang",
  vehicle: "Kecelakaan Kendaraan",
  other: "Lainnya"
};

export function IncidentOverview({ incident, onRefresh }: IncidentOverviewProps) {
  const { toast } = useToast();
  const [linkedClusters, setLinkedClusters] = useState<LinkedCluster[]>([]);
  const [priorInterventions, setPriorInterventions] = useState<PriorIntervention[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit state
  const [editedDescription, setEditedDescription] = useState(incident.description || "");
  const [editedContext, setEditedContext] = useState<Partial<AIExtractedContext>>({});
  
  // Get subscription features
  const { companyId } = useUserCompanyId();
  const { hasFeature } = useSubscriptionFeatures(companyId || undefined);
  const hasIntelligence = hasFeature('incidentIntelligence');

  // Parse AI extracted context from incident
  const aiContext: AIExtractedContext | null = incident.ai_extracted_context 
    ? (typeof incident.ai_extracted_context === 'string' 
        ? JSON.parse(incident.ai_extracted_context) 
        : incident.ai_extracted_context as AIExtractedContext)
    : null;

  // Get linked signals from AI context
  const linkedSignals: AIExtractedSignal[] = aiContext?.linked_signals || [];

  // Initialize edit state when aiContext changes
  useEffect(() => {
    if (aiContext) {
      setEditedContext({
        mechanism_of_injury: aiContext.mechanism_of_injury,
        body_part: aiContext.body_part,
        hazard_category: aiContext.hazard_category,
        work_activity: aiContext.work_activity,
        location: aiContext.location,
        inferred_severity: aiContext.inferred_severity,
      });
    }
    setEditedDescription(incident.description || "");
  }, [incident.id, aiContext]);

  useEffect(() => {
    if (hasIntelligence) {
      loadSupplementaryData();
    } else {
      setLoading(false);
    }
  }, [incident.id, hasIntelligence, aiContext]);

  const handleGenerateAIContext = async () => {
    setGeneratingAI(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-incident-ai-context", {
        body: { incident_id: incident.id },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Ringkasan AI berhasil dibuat" });
      onRefresh();
    } catch (error) {
      console.error("Error generating AI context:", error);
      toast({ 
        title: "Gagal membuat ringkasan AI",
        description: error instanceof Error ? error.message : "Silakan coba lagi",
        variant: "destructive" 
      });
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSaveEdits = async () => {
    setSaving(true);
    try {
      // Build updated AI context as plain JSON object
      const updatedAiContext = {
        people_involved: aiContext?.people_involved?.map(p => ({
          name: p.name,
          worker_id: p.worker_id,
          role_in_incident: p.role_in_incident,
          matched_confidence: p.matched_confidence
        })) || [],
        linked_signals: aiContext?.linked_signals?.map(s => ({
          type: s.type,
          id: s.id,
          title: s.title,
          relevance: s.relevance,
          risk_score: s.risk_score
        })) || [],
        mechanism_of_injury: editedContext.mechanism_of_injury || null,
        hazard_category: editedContext.hazard_category || null,
        body_part: editedContext.body_part || null,
        work_activity: editedContext.work_activity || null,
        location: editedContext.location || null,
        inferred_severity: editedContext.inferred_severity || null,
        generated_at: aiContext?.generated_at || new Date().toISOString(),
      };

      // Update both description and AI context - cast to any to satisfy Supabase's Json type
      const { error } = await supabase
        .from("workplace_incidents")
        .update({ 
          description: editedDescription,
          ai_extracted_context: updatedAiContext as any
        })
        .eq("id", incident.id);

      if (error) throw error;

      toast({ title: "Perubahan berhasil disimpan" });
      setIsEditing(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving edits:", error);
      toast({ 
        title: "Gagal menyimpan perubahan",
        description: error instanceof Error ? error.message : "Silakan coba lagi",
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedDescription(incident.description || "");
    if (aiContext) {
      setEditedContext({
        mechanism_of_injury: aiContext.mechanism_of_injury,
        body_part: aiContext.body_part,
        hazard_category: aiContext.hazard_category,
        work_activity: aiContext.work_activity,
        location: aiContext.location,
        inferred_severity: aiContext.inferred_severity,
      });
    }
  };

  const loadSupplementaryData = async () => {
    setLoading(true);
    try {
      // Get worker IDs from AI context (NOT from incident.people_involved which is null)
      const involvedWorkerIds = aiContext?.people_involved
        ?.filter(p => p.worker_id)
        .map(p => p.worker_id!) || [];

      // Fetch clusters containing involved workers (ANY cluster, not just high-risk)
      if (involvedWorkerIds.length > 0) {
        const { data: clusters } = await supabase
          .from("worker_clusters")
          .select("id, cluster_name, cluster_risk_score, member_ids")
          .eq("company_id", incident.company_id);

        // Filter to clusters that include any involved worker
        const relevantClusters = (clusters || []).filter(c => {
          const memberIds = c.member_ids as string[] || [];
          return involvedWorkerIds.some(wid => memberIds.includes(wid));
        });

        setLinkedClusters(relevantClusters.map(c => ({
          id: c.id,
          name: c.cluster_name || "Cluster",
          risk_score: c.cluster_risk_score || 0,
          worker_count: (c.member_ids as string[] || []).length,
        })));
      } else {
        setLinkedClusters([]);
      }

      // Fetch interventions for involved workers
      if (involvedWorkerIds.length > 0) {
        const { data: interventions } = await supabase
          .from("interventions")
          .select("id, title, delivery_method, status, created_at, target_worker_id")
          .in("target_worker_id", involvedWorkerIds)
          .order("created_at", { ascending: false })
          .limit(10);

        setPriorInterventions((interventions || []).map(i => ({
          id: i.id,
          title: i.title,
          type: i.delivery_method,
          target: "worker",
          status: i.status,
          delivered_at: i.created_at,
        })));
      } else {
        setPriorInterventions([]);
      }

    } catch (error) {
      console.error("Error loading supplementary data:", error);
    } finally {
      setLoading(false);
    }
  };

  const severityInfo = INJURY_SEVERITY_LABELS[incident.injury_severity as InjurySeverity];
  const areaInfo = AREA_SAFETY_LABELS[incident.area_safety_status as AreaSafetyStatus];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "fatality": return "bg-gray-900 text-white";
      case "lost_time": return "bg-red-100 text-red-800";
      case "medical_treatment": return "bg-orange-100 text-orange-800";
      case "first_aid": return "bg-yellow-100 text-yellow-800";
      default: return "bg-green-100 text-green-800";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "injured": return { label: "Korban", color: "text-red-600" };
      case "witness": return { label: "Saksi", color: "text-blue-600" };
      case "involved": return { label: "Terlibat", color: "text-amber-600" };
      case "reporter": return { label: "Pelapor", color: "text-green-600" };
      default: return { label: role, color: "text-muted-foreground" };
    }
  };

  // Generate AI insight based on extracted context
  const generateAIInsight = () => {
    if (!aiContext) return null;
    
    const parts = [];
    if (incident.was_predictable) {
      parts.push(`Insiden ini DAPAT DIPREDIKSI berdasarkan ${incident.related_hazard_count} sinyal sebelumnya.`);
    }
    if (linkedSignals.length > 0) {
      parts.push(`Ditemukan ${linkedSignals.length} sinyal terkait yang mungkin terlewat.`);
    }
    return parts.length > 0 ? parts.join(" ") : null;
  };

  const aiInsight = generateAIInsight();

  return (
    <div className="space-y-6">
      {/* Incident Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Siren className="h-5 w-5 text-primary" />
                Ringkasan Insiden
              </CardTitle>
              <CardDescription>
                {format(new Date(incident.incident_datetime), "EEEE, dd MMMM yyyy HH:mm", { locale: localeId })}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getSeverityColor(incident.injury_severity)}>
                {severityInfo?.icon} {severityInfo?.labelId}
              </Badge>
              {!isEditing && (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Description */}
          <div>
            {isEditing ? (
              <Textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className="min-h-[100px]"
                placeholder="Deskripsi insiden..."
              />
            ) : (
              <p className="text-base">{incident.description || "Tidak ada deskripsi"}</p>
            )}
          </div>

          {/* AI Extraction Details - Enhanced Section */}
          {aiContext || isEditing ? (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Brain className="h-4 w-4 text-primary" />
                    Detail Ekstraksi AI
                  </p>
                  {isEditing && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={handleCancelEdit} disabled={saving}>
                        <X className="h-4 w-4 mr-1" />
                        Batal
                      </Button>
                      <Button size="sm" onClick={handleSaveEdits} disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                        Simpan
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg bg-muted/50 border">
                  {/* People Involved - read only */}
                  {aiContext?.people_involved && aiContext.people_involved.length > 0 && (
                    <div className="space-y-2 col-span-full">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" /> Orang Terlibat (AI Detected)
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {aiContext.people_involved.map((person, idx) => {
                          const roleInfo = getRoleLabel(person.role_in_incident);
                          return (
                            <Badge 
                              key={idx} 
                              variant="secondary" 
                              className="gap-1.5 py-1"
                            >
                              <Avatar className="h-4 w-4">
                                <AvatarFallback className="text-[8px]">
                                  {person.name.slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium">{person.name}</span>
                              <span className={cn("text-xs", roleInfo.color)}>
                                ({roleInfo.label})
                              </span>
                              {person.worker_id && (
                                <CheckCircle2 className="h-3 w-3 text-green-600" />
                              )}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Editable Fields */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Zap className="h-3 w-3" /> Aktivitas Kerja
                    </p>
                    {isEditing ? (
                      <Input 
                        value={editedContext.work_activity || ""} 
                        onChange={(e) => setEditedContext(prev => ({ ...prev, work_activity: e.target.value }))}
                        placeholder="Aktivitas yang sedang dilakukan"
                      />
                    ) : (
                      <p className="font-medium text-sm">{aiContext?.work_activity || "-"}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Mekanisme Cedera
                    </p>
                    {isEditing ? (
                      <Select 
                        value={editedContext.mechanism_of_injury || ""} 
                        onValueChange={(v) => setEditedContext(prev => ({ ...prev, mechanism_of_injury: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pilih mekanisme" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(MECHANISM_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium text-sm">
                        {aiContext?.mechanism_of_injury 
                          ? MECHANISM_LABELS[aiContext.mechanism_of_injury] || aiContext.mechanism_of_injury 
                          : "-"}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" /> Bagian Tubuh
                    </p>
                    {isEditing ? (
                      <Input 
                        value={editedContext.body_part || ""} 
                        onChange={(e) => setEditedContext(prev => ({ ...prev, body_part: e.target.value }))}
                        placeholder="Bagian tubuh yang cedera"
                      />
                    ) : (
                      <p className="font-medium text-sm">{aiContext?.body_part || "-"}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Shield className="h-3 w-3" /> Kategori Bahaya
                    </p>
                    {isEditing ? (
                      <Select 
                        value={editedContext.hazard_category || ""} 
                        onValueChange={(v) => setEditedContext(prev => ({ ...prev, hazard_category: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pilih kategori" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(HAZARD_CATEGORY_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium text-sm">
                        {aiContext?.hazard_category 
                          ? HAZARD_CATEGORY_LABELS[aiContext.hazard_category] || aiContext.hazard_category 
                          : "-"}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Lokasi (AI)
                    </p>
                    {isEditing ? (
                      <Input 
                        value={editedContext.location || ""} 
                        onChange={(e) => setEditedContext(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="Lokasi insiden"
                      />
                    ) : (
                      <p className="font-medium text-sm">{aiContext?.location || "-"}</p>
                    )}
                  </div>

                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Activity className="h-3 w-3" /> Tingkat Keparahan (AI)
                    </p>
                    {isEditing ? (
                      <Select 
                        value={editedContext.inferred_severity || ""} 
                        onValueChange={(v) => setEditedContext(prev => ({ ...prev, inferred_severity: v as InjurySeverity }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pilih keparahan" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(INJURY_SEVERITY_LABELS).map(([key, info]) => (
                            <SelectItem key={key} value={key}>{info.labelId}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-medium text-sm">
                        {aiContext?.inferred_severity 
                          ? INJURY_SEVERITY_LABELS[aiContext.inferred_severity]?.labelId 
                          : "-"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <Separator />
              <div className="p-4 rounded-lg border border-dashed bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Ringkasan AI Belum Tersedia</p>
                      <p className="text-xs text-muted-foreground">
                        Klik untuk mengekstrak detail insiden menggunakan AI
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleGenerateAIContext}
                    disabled={generatingAI}
                    className="gap-2"
                  >
                    {generatingAI ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {generatingAI ? "Memproses..." : "Generate Ringkasan"}
                  </Button>
                </div>
              </div>
            </>
          )}

          <Separator />

          {/* Key Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Lokasi
              </p>
              <p className="font-medium">{incident.location}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> Shift
              </p>
              <p className="font-medium">{incident.shift || "-"}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Activity className="h-3 w-3" /> Status Area
              </p>
              <Badge variant="outline" className={areaInfo?.color}>
                {areaInfo?.icon} {areaInfo?.labelId}
              </Badge>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <FileText className="h-3 w-3" /> Status
              </p>
              <Badge variant="secondary">{incident.status}</Badge>
            </div>
          </div>

          {/* Injury Details */}
          {incident.injury_severity !== "no_injury" && (
            <>
              <Separator />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {incident.body_part && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Bagian Tubuh</p>
                    <p className="font-medium">{incident.body_part}</p>
                  </div>
                )}
                {incident.mechanism_of_injury && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Mekanisme Cedera</p>
                    <p className="font-medium">{incident.mechanism_of_injury}</p>
                  </div>
                )}
                {incident.immediate_treatment && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Penanganan</p>
                    <p className="font-medium">{incident.immediate_treatment}</p>
                  </div>
                )}
                {incident.lost_time_days && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Hari Hilang</p>
                    <p className="font-medium text-destructive">{incident.lost_time_days} hari</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Photo Gallery */}
          {incident.photo_urls && incident.photo_urls.length > 0 && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Image className="h-4 w-4" />
                  Foto Bukti ({incident.photo_urls.length})
                </p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {incident.photo_urls.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Evidence ${idx + 1}`}
                      className="h-24 w-24 object-cover rounded-lg border"
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* SORIN Intelligence Card */}
      {hasIntelligence ? (
        <Card className={cn(
          "border-l-4",
          incident.was_predictable ? "border-l-warning bg-warning/5" : "border-l-primary"
        )}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              SORIN Intelligence
            </CardTitle>
            <CardDescription>
              Sinyal, cluster, dan intervensi terkait insiden ini
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Predictability Warning */}
            {incident.was_predictable && (
              <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
                <div className="flex items-center gap-2 text-warning font-medium">
                  <AlertTriangle className="h-5 w-5" />
                  Insiden ini DAPAT DIPREDIKSI
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {incident.related_hazard_count} laporan serupa ditemukan sebelum insiden terjadi.
                </p>
              </div>
            )}

            {/* AI Insight */}
            {aiInsight && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm">{aiInsight}</p>
                </div>
              </div>
            )}

            {/* Linked Signals from AI Context */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Sinyal Terkait ({linkedSignals.length})
              </p>
              {linkedSignals.length === 0 ? (
                <p className="text-sm text-muted-foreground pl-6">Tidak ada sinyal terkait ditemukan</p>
              ) : (
                <div className="space-y-2">
                  {linkedSignals.map((signal, idx) => (
                    <div
                      key={signal.id || idx}
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {signal.type === "hazard_report" && "Laporan Bahaya"}
                          {signal.type === "inspection_finding" && "Temuan Inspeksi"}
                          {signal.type === "worker_diagnostic" && "Diagnostik Pekerja"}
                          {signal.type === "cluster" && "Cluster Berisiko"}
                        </Badge>
                        <div>
                          <p className="text-sm font-medium line-clamp-1">{signal.title}</p>
                          <p className="text-xs text-muted-foreground">{signal.relevance}</p>
                        </div>
                      </div>
                      {signal.risk_score && (
                        <Badge variant="destructive" className="text-xs">
                          {signal.risk_score}%
                        </Badge>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Linked Clusters */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Network className="h-4 w-4" />
                Cluster Terkait ({linkedClusters.length})
              </p>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pl-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Memuat...
                </div>
              ) : linkedClusters.length === 0 ? (
                <p className="text-sm text-muted-foreground pl-6">Tidak ada cluster terkait</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {linkedClusters.map((cluster) => (
                    <Badge
                      key={cluster.id}
                      variant="secondary"
                      className={cn(
                        "gap-1",
                        cluster.risk_score >= 60 && "bg-destructive/10 text-destructive border-destructive/30"
                      )}
                    >
                      <TrendingUp className="h-3 w-3" />
                      {cluster.name} ({cluster.risk_score}%)
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Prior Interventions */}
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Intervensi Sebelumnya ({priorInterventions.length})
              </p>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground pl-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Memuat...
                </div>
              ) : priorInterventions.length === 0 ? (
                <p className="text-sm text-muted-foreground pl-6">Tidak ada intervensi ditemukan</p>
              ) : (
                <div className="space-y-2">
                  {priorInterventions.map((intervention) => (
                    <div
                      key={intervention.id}
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {intervention.type}
                        </Badge>
                        <div>
                          <p className="text-sm font-medium line-clamp-1">{intervention.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(intervention.delivered_at), "dd MMM yyyy")}
                          </p>
                        </div>
                      </div>
                      <Badge 
                        variant="outline"
                        className={cn(
                          "text-xs",
                          intervention.status === "completed" && "text-green-600",
                          intervention.status === "pending" && "text-amber-600"
                        )}
                      >
                        {intervention.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Enterprise Tier Gated Placeholder */
        <Card className="border-dashed">
          <CardContent className="py-8">
            <div className="text-center space-y-3">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Lock className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <h4 className="font-semibold">SORIN Intelligence</h4>
                <p className="text-sm text-muted-foreground">
                  Fitur {TIER_NAMES.enterprise} - Dapatkan analisis AI tentang sinyal terkait, 
                  cluster berisiko, dan intervensi sebelumnya.
                </p>
              </div>
              <Button variant="outline" size="sm" className="gap-2">
                Upgrade ke Enterprise
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regulatory Info */}
      {(incident.lost_time_indicator || incident.is_wajib_lapor) && (
        <Card className="border-l-4 border-l-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Informasi Regulasi
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {incident.lost_time_indicator && (
              <div className="flex items-center gap-2">
                <Badge variant="destructive">LTI</Badge>
                <span className="text-sm">Lost Time Injury - Wajib dilaporkan</span>
              </div>
            )}
            {incident.is_wajib_lapor && (
              <div className="flex items-center gap-2">
                <Badge variant="destructive">Wajib Lapor</Badge>
                <span className="text-sm">Insiden wajib dilaporkan ke regulator</span>
              </div>
            )}
            {incident.regulator_notified_at && (
              <p className="text-sm text-muted-foreground">
                Dilaporkan: {format(new Date(incident.regulator_notified_at), "dd MMM yyyy HH:mm")}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
